import { Component, DestroyRef, inject, Input, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
	FormControl,
	Validators,
	AbstractControl,
	ValidationErrors,
	FormsModule,
	ReactiveFormsModule,
} from '@angular/forms';
import { MatDivider } from '@angular/material/list';
import { MatFormField, MatLabel } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';

@Component({
	selector: 'app-add-client-details',
	templateUrl: './add-client-details.component.html',
	styleUrls: [
		'./add-client-details.component.scss',
		'../../common/main.scss',
		'../../common/stepper.scss',
	],
	imports: [
		MatDivider,
		MatFormField,
		MatLabel,
		MatInput,
		FormsModule,
		ReactiveFormsModule,
	],
})
export class AddClientDetailsComponent implements OnInit {
	@Input() editClient!: EditClient;
	@Input() selectedProperty?: any; // Pre-selected property data with address information

	// Validation patterns with Unicode support
	// Name pattern: requires at least one Unicode letter (any language), allows special characters and numbers
	private readonly nameRegex = new RegExp('^(?=.*\\p{L}).+$', 'u');
	// IDN pattern: allows Unicode letters and numbers only
	private readonly idnRegex = new RegExp('^[\\p{L}0-9]+$', 'u');

	// Form controls with custom validators
	firstNameControl!: FormControl;
	lastNameControl!: FormControl;
	idnControl!: FormControl;

	private readonly destroyRef = inject(DestroyRef);

	ngOnInit(): void {
		// Initialize form controls with custom validators
		this.firstNameControl = new FormControl(this.editClient.firstName || '', [
			Validators.required,
			this.nameValidator.bind(this),
		]);

		this.lastNameControl = new FormControl(this.editClient.lastName || '', [
			Validators.required,
			this.nameValidator.bind(this),
		]);

		this.idnControl = new FormControl(this.editClient.idn || '', [
			Validators.required,
			this.idnValidator.bind(this),
		]);

		// Trigger validation on initial values (important when navigating back to pre-filled form)
		this.firstNameControl.updateValueAndValidity();
		this.lastNameControl.updateValueAndValidity();
		this.idnControl.updateValueAndValidity();

		// Sync form control values with editClient model
		this.firstNameControl.valueChanges
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe((value) => {
				this.editClient.firstName = value;
			});

		this.lastNameControl.valueChanges
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe((value) => {
				this.editClient.lastName = value;
			});

		this.idnControl.valueChanges
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe((value) => {
				this.editClient.idn = value;
			});
	}

	// Custom validator for names (Unicode letters, no numbers)
	private nameValidator(control: AbstractControl): ValidationErrors | null {
		const value = control.value;
		if (!value) {
			return null; // Let required validator handle empty values
		}
		return this.nameRegex.test(value) ? null : { invalidName: true };
	}

	// Custom validator for IDN (Unicode letters and numbers)
	private idnValidator(control: AbstractControl): ValidationErrors | null {
		const value = control.value;
		if (!value) {
			return null; // Let required validator handle empty values
		}
		return this.idnRegex.test(value) ? null : { invalidIdn: true };
	}

	public get isValid(): boolean {
		return (
			this.firstNameControl?.valid &&
			this.lastNameControl?.valid &&
			this.idnControl?.valid
		);
	}

	public isFirstNameValid(): boolean {
		return this.firstNameControl?.valid ?? false;
	}

	public isLastNameValid(): boolean {
		return this.lastNameControl?.valid ?? false;
	}

	public isIdnValid(): boolean {
		return this.idnControl?.valid ?? false;
	}
}

export type EditClient = {
	id?: string;
	companyId?: string;
	firstName?: string;
	lastName?: string;
	idn?: string;
	locationId?: string;
	active?: boolean;
};

export const emptyEditClient = (): EditClient => ({
	active: true,
});
